import {
    attachmentService,
    AuthContext,
    trelloStorageService
} from "@harvestr-api/common";
import {
    gqlAttachment,
    gqlDiscovery,
    gqlTrelloCard,
    gqlTrelloCardCreateWithoutDiscoveryInput
} from "@harvestr-api/models";
import {
    htmlToMarkdown,
    logger,
    parallelPromiseHelper,
    toClientId
} from "@harvestr-api/utils";
import * as Q from "q";
import request from "request-promise";
import { URL } from "url";
import {
    getTrelloAuth,
    getTrelloClient,
    getTrelloToken
} from "../helpers/trello-client.helper";

export const trelloApiService = {
    async createCard(
        data: {
            harvestrCard: gqlTrelloCardCreateWithoutDiscoveryInput;
            discovery: Pick<gqlDiscovery, "id" | "title" | "clientId">;
            copyDiscoveryAttachments: boolean;
        },
        ctx: AuthContext
    ) {
        const deferred = Q.defer<any>();
        const trello = await getTrelloClient(ctx);

        const validatedHarvestrCard = trelloApiService.validateTrelloCard(
            data.harvestrCard
        );

        trello.addCard(
            validatedHarvestrCard.trelloName,
            validatedHarvestrCard.trelloDesc,
            validatedHarvestrCard.trelloListId,
            async (error, trelloCard: any) => {
                if (error) {
                    logger.info("Could not add card:", error);
                    deferred.reject(error);
                } else {
                    const result_card = await trelloApiService.linkCard(
                        {
                            harvestrCard: {
                                ...validatedHarvestrCard,
                                trelloId: trelloCard.id
                            },
                            discovery: data.discovery,
                            copyDiscoveryAttachments:
                                data.copyDiscoveryAttachments
                        },

                        ctx
                    );

                    deferred.resolve(result_card);
                }
            }
        );
        return deferred.promise;
    },

    async linkCard(
        data: {
            harvestrCard: gqlTrelloCardCreateWithoutDiscoveryInput;
            discovery: Pick<gqlDiscovery, "id" | "title" | "clientId">;
            copyDiscoveryAttachments: boolean;
        },
        ctx: AuthContext
    ) {
        const canAddAttachment =
            data.harvestrCard.trelloId !== undefined &&
            String(data.harvestrCard.trelloId) !== "-1";

        if (canAddAttachment) {
            const setupDiscoveryAttachments = async () => {
                if (data.copyDiscoveryAttachments === false) {
                    return [];
                }
                const discoveryAttachments: Array<gqlAttachment> = await attachmentService.getAttachmentsOfDiscovery(
                    { discoveryId: data.discovery.id }
                );
                return (discoveryAttachments || [])
                    .filter(attachment => {
                        try {
                            new URL(attachment.url);
                            return true;
                        } catch (err) {
                            return false;
                        }
                    })
                    .map((attachment: gqlAttachment) => {
                        return {
                            url: attachment.url,
                            name: `[Harvestr] ${attachment.filename}`
                        };
                    });
            };

            await trelloApiService.addAttachmentToCard(
                data.harvestrCard.trelloId,
                [
                    {
                        url: `${process.env.H_CLIENT_URL}/components/0/list/${
                            toClientId(data.discovery).id
                        }`,
                        name: `[Harvestr] ${data.discovery.title}`
                    },
                    ...(await setupDiscoveryAttachments())
                ],
                ctx
            );
        }

        const cardFromTrello = await trelloApiService.getCard(
            data.harvestrCard.trelloId,
            false,
            ctx
        );

        return trelloApiService.toHarvestrTrelloCard(
            cardFromTrello,
            data.harvestrCard.id as string
        );
    },

    async addAttachmentToCard(
        cardId: string,
        attachments: Array<any>,
        ctx: AuthContext
    ) {
        const listAttachmentsOfCard = await trelloApiService.getAttachmentsOfCard(
            cardId,
            ctx
        );
        const attachmentsToSet = attachments.filter(candidateAttachment => {
            const foundAttachment = (listAttachmentsOfCard || []).some(
                cardAtt =>
                    cardAtt.url === candidateAttachment.url &&
                    cardAtt.name === candidateAttachment.name
            );
            return foundAttachment === false;
        });

        if (attachmentsToSet.length > 0) {
            const trelloClient = await getTrelloClient(ctx);
            return Promise.all(
                attachmentsToSet.map(att => {
                    return trelloClient.makeRequest(
                        "post",
                        `/1/cards/${cardId}/attachments`,
                        {
                            url: att.url,
                            name: att.name
                        }
                    );
                })
            );
        }
        return Promise.resolve(true);
    },

    async getList(list_id: string, ctx: AuthContext) {
        const deferred = Q.defer<any>();

        const options = {
            method: "GET",
            url: `https://api.trello.com/1/lists/${list_id}`,
            qs: await getTrelloAuth(ctx)
        };

        request(options, (error, response, body) => {
            try {
                if (error) {
                    throw new Error(error);
                }
                deferred.resolve(JSON.parse(body));
            } catch (err) {
                deferred.reject(err);
            }
        });
        return deferred.promise;
    },

    async getBoard(id: string, ctx: AuthContext) {
        const deferred = Q.defer();

        const options = {
            method: "GET",
            url: `https://api.trello.com/1/boards/${id}`,
            qs: await getTrelloAuth(ctx)
        };

        request(options, (error, response, body) => {
            try {
                if (error) {
                    throw new Error(error);
                }
                deferred.resolve(JSON.parse(body));
            } catch (err) {
                deferred.reject(err);
            }
        });
        return deferred.promise;
    },

    async hasUserRequiredPermissions(
        projectId: string,
        gotTokenAlready?: string
    ) {
        const TRELLO_USER_MEMBER_TYPES_ALLOWED = ["normal"];
        const auth = await getTrelloAuth(
            {
                project_id: projectId,
                type: "INTEGRATION_TRELLO"
            },
            gotTokenAlready
        );
        return new Promise((resolve, reject) => {
            const options = {
                method: "GET",
                url: `https://api.trello.com/1/members/me`,
                qs: auth
            };

            request(options, (error, response, rawBody) => {
                try {
                    if (error) {
                        reject(error);
                    } else {
                        const body =
                            typeof rawBody === "string"
                                ? JSON.parse(rawBody)
                                : rawBody;
                        const hasRequiredPermission = TRELLO_USER_MEMBER_TYPES_ALLOWED.includes(
                            body.memberType
                        );
                        resolve(hasRequiredPermission);
                    }
                } catch (err) {
                    reject(err);
                }
            });
        });
    },

    async hasIntegrationEnabled(projectId: string): Promise<boolean> {
        const integrationToken = await getTrelloToken({
            project_id: projectId,
            type: "INTEGRATION_TRELLO"
        });
        return integrationToken !== undefined;
    },

    async getCard(card_id: string, asHarvestrCard = false, ctx: AuthContext) {
        const authQS = await getTrelloAuth(ctx);

        const options = {
            method: "GET",
            url: `https://api.trello.com/1/cards/${card_id}`,
            qs: authQS
        };

        return new Promise((resolve, reject) => {
            request(options, async (error, response, body) => {
                if (
                    !(response.statusCode >= 200 && response.statusCode <= 299)
                ) {
                    reject({ statusCode: response.statusCode });
                } else {
                    try {
                        if (error) {
                            throw new Error(error);
                        }
                        if (!body) throw new Error("Trello card not found");

                        const cards = JSON.parse(body);
                        const list = await trelloApiService.getList(
                            cards.idList,
                            ctx
                        );
                        const board = await trelloApiService.getBoard(
                            cards.idBoard,
                            ctx
                        );
                        const card = {
                            ...cards,
                            list: list ? list : null,
                            board: board ? board : null
                        };
                        if (asHarvestrCard === true) {
                            resolve(
                                trelloApiService.toHarvestrTrelloCard(card)
                            );
                        } else {
                            resolve(card);
                        }
                    } catch (err) {
                        reject({ statusCode: response.statusCode });
                    }
                }
            });
        });

        //return deferred.promise;
    },

    async getAttachmentsOfCard(cardId: string, ctx: AuthContext) {
        const trello = await getTrelloClient(ctx);
        const attachments = await trello.makeRequest(
            "get",
            `/1/cards/${cardId}/attachments`
        );
        return attachments;
    },

    async getBoards(ctx: AuthContext) {
        const trello = await getTrelloClient(ctx);
        return trello.makeRequest("get", "/1/members/me/boards", {
            filter: "open",
            fields: ["name", "desc", "dateLastView"]
        });
    },

    async getLists(board_id: string, ctx: AuthContext) {
        const trello = await getTrelloClient(ctx);
        return trello.getListsOnBoard(board_id);
    },

    async getCards(list_id: string, asHarvestrCard = false, ctx: AuthContext) {
        const trello = await getTrelloClient(ctx);
        const cards = await trello.getCardsOnList(list_id);
        return asHarvestrCard === false
            ? cards
            : cards.map(c => trelloApiService.toHarvestrTrelloCard(c));
    },

    validateTrelloCard(
        trelloCard: gqlTrelloCardCreateWithoutDiscoveryInput
    ): gqlTrelloCardCreateWithoutDiscoveryInput {
        const markdownService = htmlToMarkdown({
            rules: {
                strikethrough: {
                    filter: ["s"],
                    replacement: content => "~~" + content + "~~"
                }
            }
        });
        return {
            ...trelloCard,
            trelloDesc: markdownService.htmlToMarkdown(
                trelloCard?.trelloDesc || ""
            )
        };
    },

    toHarvestrTrelloCard(
        cardFromTrello: any,
        clientId?: string
    ): gqlTrelloCardCreateWithoutDiscoveryInput {
        return {
            id: clientId,
            trelloId: cardFromTrello?.id || 0,
            trelloIdShort: cardFromTrello?.idShort || 0,
            trelloName: cardFromTrello?.name || "",
            trelloDesc: cardFromTrello?.desc || "",
            trelloPos: cardFromTrello?.pos || 0,
            trelloClose: cardFromTrello?.closed || false,
            trelloDue: cardFromTrello?.due || null,
            trelloShortLink: cardFromTrello?.shortLink || "",
            trelloUrl: cardFromTrello?.url || "",
            trelloShortUrl: cardFromTrello?.shortUrl || "",
            trelloBoardId:
                cardFromTrello?.board?.id || cardFromTrello?.idBoard || "",
            trelloBoardName: cardFromTrello?.board?.name || "",
            trelloListId:
                cardFromTrello?.list?.id || cardFromTrello?.idList || "",
            trelloListName: cardFromTrello?.list?.name || "",
            trelloListPosition: cardFromTrello?.list?.pos || -1,
            trelloLabels: (Array.isArray(cardFromTrello?.labels)
                ? cardFromTrello?.labels
                : []
            )
                .filter(l => l !== null)
                .map(label =>
                    typeof label === "string"
                        ? { id: 0, color: label, name: label }
                        : {
                              id: label?.id,
                              color: label?.color,
                              name: label?.name
                          }
                ),
            trelloBadgesComments: (cardFromTrello?.comments || []).length,
            trelloBadgesAttachments: (cardFromTrello?.attachment || []).length
        };
    },

    toTrelloCard(harvestrCard: gqlTrelloCardCreateWithoutDiscoveryInput) {
        return {
            id: harvestrCard.trelloId,
            board: {
                id: harvestrCard.trelloBoardId,
                name: harvestrCard.trelloBoardId
            },
            list: {
                id: harvestrCard.trelloListId,
                name: harvestrCard.trelloListName
            },
            labels:
                Array.isArray(harvestrCard.trelloListId) &&
                harvestrCard.trelloListId.length > 0
                    ? harvestrCard.trelloListId
                    : 0,
            name: harvestrCard.trelloName,
            desc: harvestrCard.trelloDesc
        };
    },

    async syncIssues(
        where: { projectsIds: string[] },
        apis: {
            devtools: {
                BASE_REDIS_ISSUES_SYNC_KEY: Readonly<string>;
                canSyncToday: (
                    devtoolName: string,
                    redisKey: string
                ) => Promise<boolean>;
            };
        }
    ) {
        console.log("trello.syncIssues() --------------------");

        console.log(
            "trello.syncIssues() : where.projectsIds : ",
            where.projectsIds
        );

        const tasks = where.projectsIds.map(async projectId => {
            const integrationStateEnabled = await trelloApiService.hasIntegrationEnabled(
                projectId
            );
            if (integrationStateEnabled === false) {
                return [];
            } else {
                const REDIS_KEY_LAST_SYNC = `${apis.devtools.BASE_REDIS_ISSUES_SYNC_KEY}${projectId}`;
                const canSyncNow = await apis.devtools.canSyncToday(
                    "trello",
                    REDIS_KEY_LAST_SYNC
                );
                if (canSyncNow === false) {
                    console.log(
                        `trello : sync OFF for projectId ${projectId} : last sync less than 24h`
                    );
                    return [];
                } else {
                    console.log(`trello : sync ON for projectId ${projectId}`);
                    const cards = (
                        await trelloStorageService.getCardsOfProjectForSync({
                            projectId
                        })
                    ).filter(
                        card =>
                            String(card.trelloId) !== "0" &&
                            card.trelloId !== null
                    );

                    return trelloApiService.bulkSyncIssuesOfProjects(cards, {
                        project_id: projectId,
                        type: "INTEGRATION_TRELLO"
                    });
                }
            }
        });

        console.log("trello.syncIssues(): END");
        return tasks;
    },
    async bulkSyncIssuesOfProjects(
        hvrCards: gqlTrelloCard[],
        ctx: AuthContext
    ) {
        return parallelPromiseHelper.uploadData(
            hvrCards,
            async hvrCardsChunk => {
                return hvrCardsChunk.map(async hvrCard => {
                    try {
                        const cardFromTrello: any = await trelloApiService.getCard(
                            hvrCard.trelloId,
                            false,
                            ctx
                        );
                        if (
                            cardFromTrello !== undefined &&
                            cardFromTrello?.id !== "" &&
                            cardFromTrello?.trelloId !== undefined &&
                            cardFromTrello?.trelloId !== null &&
                            cardFromTrello?.trelloId !== ""
                        ) {
                            return trelloStorageService.updateCard(
                                cardFromTrello.id,
                                trelloApiService.toHarvestrTrelloCard(
                                    cardFromTrello,
                                    undefined
                                )
                            );
                        } else {
                            return Promise.resolve();
                        }
                    } catch (error) {
                        if (
                            error.statusCode === 401 ||
                            error.statusCode === 404 ||
                            error.statusCode === 429
                        ) {
                            console.log(
                                "card not found or over-rate limit : trelloId : ",
                                hvrCard.trelloId
                            );
                            return false;
                        }
                    }
                });
            }
        );
    }
};
